import React, { useEffect } from "react";
import CloseIcon from "../../../assets/images/closeIcon.svg";
import { classes } from "@/utils/ui";
interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    size?: "sm" | "md" | "lg" | "xl" | "full";
    variant?: "default" | "dark" | "light";
    padding?: "none" | "sm" | "md" | "lg" | "xl";
    closeOnOverlayClick?: boolean;
    showCloseButton?: boolean;
    className?: string;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    title,
    variant = "dark",
    padding = "md",
    closeOnOverlayClick = true,
    showCloseButton = true,
    className = "",
}) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const variantClasses: Record<NonNullable<ModalProps["variant"]>, string> = {
        default: "bg-white border-gray-200 text-gray-900",
        dark: "bg-bgCard border-cardBorder text-white",
        light: "bg-gray-50 border-gray-300 text-gray-900",
    };

    const paddingClasses: Record<NonNullable<ModalProps["padding"]>, string> = {
        none: "",
        sm: "p-3",
        md: "p-6",
        lg: "p-8",
        xl: "p-10",
    };

    const outerCornerBorders = `
    after:h-1 after:w-1 after:top-0 after:left-0 after:border-t after:border-l after:border-accentPurple after:absolute 
    before:h-1 before:w-1 before:top-0 before:right-0 before:border-t before:border-r before:border-accentPurple before:absolute
  `;

    const innerCornerBorders = `
    after:h-1 after:w-1 after:bottom-0 after:left-0 after:border-b after:border-l after:border-accentPurple after:absolute 
    before:h-1 before:w-1 before:bottom-0 before:right-0 before:border-b before:border-r before:border-accentPurple before:absolute
  `;

    const modalWrapperClass = `relative ${outerCornerBorders} ${className}`;
    const modalContentClass = `
    ${variantClasses[variant]} 
    border 
    ${paddingClasses[padding]} 
    ${innerCornerBorders}
    w-full  rounded-lg
  `;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                {/* Overlay */}
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={closeOnOverlayClick ? onClose : undefined}
                />

                {/* Modal Container */}
                <div className={modalWrapperClass}>
                    <div className={modalContentClass}>
                        {/* Header */}
                        {(title || showCloseButton) && (
                            <div
                                className={classes(
                                    "flex items-center justify-between relative",
                                    title && "mb-2"
                                )}
                            >
                                {title && (
                                    <h3 className="text-xl font-medium text-white">
                                        {title}
                                    </h3>
                                )}
                                {showCloseButton && (
                                    <button
                                        onClick={onClose}
                                        className="cursor-pointer h-7 w-8 flex items-center justify-center absolute top-0 right-0"
                                    >
                                        <img src={CloseIcon} alt="CloseIcon" />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Content */}
                        <div>{children}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Modal;
